import type { CSSProperties, ReactNode } from "react";
import { Ico } from "../lib/icons";
import { PROTO } from "../lib/protocols";

// ─── Skeleton (loading placeholder)
// Visual styling lives in styles.css (`.skel` already exists — shimmer
// gradient over var(--bg-3)). The component just sets size/shape so we
// don't have to repeat the className+inline-style boilerplate at call
// sites.
export function Skeleton({
  width = "100%",
  height = 14,
  radius = 6,
  style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  style?: CSSProperties;
}) {
  return (
    <span
      className="skel"
      aria-hidden
      style={{
        display: "inline-block",
        width,
        height,
        borderRadius: radius,
        ...(style ?? {}),
      }}
    />
  );
}

// ─── Card surface
export function Card({
  children,
  style = {},
  inset = false,
  className = "",
  onClick,
  ...rest
}: {
  children?: ReactNode;
  style?: CSSProperties;
  inset?: boolean;
  className?: string;
  onClick?: () => void;
} & Record<string, unknown>) {
  return (
    <div
      {...rest}
      onClick={onClick}
      className={`tg-card ${inset ? "inset" : ""} ${className}`.trim()}
      style={style}
    >
      {children}
    </div>
  );
}

// ─── Pill / chip
export function Chip({
  children,
  active,
  onClick,
  style = {},
  danger,
  warning,
  success,
}: {
  children?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
  danger?: boolean;
  warning?: boolean;
  success?: boolean;
}) {
  const c = active
    ? { bg: "var(--text)", fg: "var(--bg)" }
    : danger
      ? { bg: "rgba(239,68,68,0.16)", fg: "#FCA5A5" }
      : warning
        ? { bg: "rgba(245,158,11,0.16)", fg: "#FBBF24" }
        : success
          ? { bg: "rgba(34,197,94,0.16)", fg: "#4ADE80" }
          : { bg: "var(--bg-3)", fg: "var(--text)" };
  return (
    <button
      onClick={onClick}
      style={{
        border: "none",
        cursor: "pointer",
        background: c.bg,
        color: c.fg,
        fontFamily: "var(--font-text)",
        fontSize: 13,
        fontWeight: 600,
        padding: "7px 12px",
        borderRadius: 999,
        letterSpacing: "-0.005em",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ─── Status badge
export type SwapStatusKey = "pending" | "submitted" | "completed" | "failed" | "refunded";

export function StatusBadge({ status, size = "sm" }: { status: SwapStatusKey; size?: "sm" | "lg" }) {
  const map: Record<SwapStatusKey, { color: string; bg: string; label: string; pulse?: boolean }> = {
    pending: { color: "#F59E0B", bg: "rgba(245,158,11,0.18)", label: "Pending", pulse: true },
    submitted: { color: "#3B82F6", bg: "rgba(59,130,246,0.18)", label: "Submitted" },
    completed: { color: "#22C55E", bg: "rgba(34,197,94,0.18)", label: "Completed" },
    failed: { color: "#EF4444", bg: "rgba(239,68,68,0.18)", label: "Failed" },
    refunded: { color: "#94A3B8", bg: "rgba(148,163,184,0.18)", label: "Refunded" },
  };
  const s = map[status];
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: s.bg,
        color: s.color,
        padding: size === "sm" ? "3px 8px 3px 7px" : "5px 10px 5px 9px",
        borderRadius: 999,
        fontSize: size === "sm" ? 12 : 13,
        fontWeight: 600,
      }}
    >
      <span
        className={s.pulse ? "dot-pulse" : ""}
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: s.color,
        }}
      />
      {s.label}
    </div>
  );
}

// ─── Protocol badge (dot + name)
export function ProtocolBadge({ id, size = "sm" }: { id: string; size?: "sm" | "lg" }) {
  const p = PROTO[id] ?? PROTO.rango!;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: size === "sm" ? 13 : 14,
        fontWeight: 600,
        color: "var(--text)",
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: p.color,
          boxShadow: `0 0 0 3px ${p.color}33`,
        }}
      />
      {p.name}
    </div>
  );
}

// ─── Spinner
export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="spin" style={{ display: "block" }}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ─── Bot avatar
export function BotAvatar({ size = 26 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "var(--accent)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "#0E1116",
      }}
    >
      <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 24 24" fill="none">
        <path
          d="M5 8h11M16 8l-3-3M16 8l-3 3M19 16H8M8 16l3-3M8 16l3 3"
          stroke="#0E1116"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// ─── Main Button (Telegram-native style)
export type MainButtonProps = {
  text?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  secondary?: boolean;
  color?: string;
  destructive?: boolean;
};

export function MainButton({
  text,
  disabled,
  loading,
  onClick,
  secondary,
  color,
  destructive,
}: MainButtonProps) {
  const bg = destructive
    ? "var(--danger)"
    : disabled
      ? "var(--bg-3)"
      : secondary
        ? "var(--btn-secondary-bg)"
        : color ?? "var(--btn-primary-bg)";
  const fg = destructive
    ? "#fff"
    : disabled
      ? "var(--hint)"
      : secondary
        ? "var(--btn-secondary-fg)"
        : "var(--btn-primary-fg)";
  return (
    <button
      onClick={!disabled && !loading ? onClick : undefined}
      style={{
        width: "100%",
        height: 52,
        borderRadius: "var(--r-btn)",
        border: "none",
        cursor: disabled ? "default" : "pointer",
        background: bg,
        color: fg,
        fontSize: 16,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        fontFamily: "var(--font-text)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        boxShadow: "none",
        position: "relative",
        overflow: "hidden",
        transition: "opacity 150ms ease",
      }}
    >
      {loading && <Spinner size={18} />}
      <span>{text}</span>
    </button>
  );
}

// ─── Bottom Nav
export type BottomNavKey =
  | "wallet"
  | "send"
  | "swap"
  | "contacts"
  | "receive"
  | "security"
  | "settings";

import { createContext, useContext } from "react";
export const BottomNavContext = createContext<((k: BottomNavKey) => void) | null>(null);

// 22×22 line icons. All use currentColor so the parent button's `color`
// flows through — active vs inactive tinting still works via the existing
// color logic below. strokeWidth varies (1.5 inactive / 2 active) for a
// subtle bold-on-select cue.
function NavIcon({ name, active }: { name: BottomNavKey; active: boolean }) {
  const sw = active ? 2 : 1.5;
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: sw,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    style: { display: "block" },
  };
  switch (name) {
    case "wallet":
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="13" rx="2.5" />
          <path d="M3 10h18" />
          <circle cx="17" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
        </svg>
      );
    case "send":
      // Arrow pointing up-right — diagonal shaft + arrowhead
      return (
        <svg {...common}>
          <path d="M7 17L17 7" />
          <path d="M9 7h8v8" />
        </svg>
      );
    case "swap":
      // Two horizontal arrows stacked; top points left, bottom points right
      return (
        <svg {...common}>
          <path d="M17 8H4" />
          <path d="M8 4L4 8l4 4" />
          <path d="M7 16h13" />
          <path d="M16 12l4 4-4 4" />
        </svg>
      );
    case "receive":
      // Arrow pointing down-left
      return (
        <svg {...common}>
          <path d="M17 7L7 17" />
          <path d="M15 17H7V9" />
        </svg>
      );
    case "contacts":
      // Head + shoulders person glyph
      return (
        <svg {...common}>
          <circle cx="12" cy="8.5" r="3.5" />
          <path d="M5 19c1.2-3.5 4-5.5 7-5.5s5.8 2 7 5.5" />
        </svg>
      );
    case "security":
      // Shield with check inside
      return (
        <svg {...common}>
          <path d="M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6l8-3z" />
          <path d="M9 12.2l2.2 2.2L15 10.8" />
        </svg>
      );
    case "settings":
      // Gear — inner hub + eight teeth as a single ring path. We trace
      // each tooth as a short outward stub so the stroke-width stays
      // consistent with the other nav icons (1.5 inactive / 2 active).
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v2.5M12 19.5V22M22 12h-2.5M4.5 12H2M19.07 4.93l-1.77 1.77M6.7 17.3l-1.77 1.77M19.07 19.07l-1.77-1.77M6.7 6.7L4.93 4.93" />
        </svg>
      );
  }
}

export function BottomNav({
  active,
  onSelect,
  hint,
}: {
  active: BottomNavKey;
  onSelect?: (k: BottomNavKey) => void;
  /** Optional tab to render a small pulsing dot on (onboarding signal). */
  hint?: BottomNavKey;
}) {
  const ctxSelect = useContext(BottomNavContext);
  const pick = onSelect ?? ctxSelect ?? (() => undefined);
  const items: { key: BottomNavKey; label: string }[] = [
    { key: "wallet", label: "Wallet" },
    { key: "send", label: "Send" },
    { key: "swap", label: "Swap" },
    { key: "receive", label: "Receive" },
    { key: "contacts", label: "Contacts" },
    { key: "security", label: "Security" },
    { key: "settings", label: "Settings" },
  ];
  return (
    <div style={{ display: "flex", padding: "4px 0 14px", gap: 4 }}>
      {items.map((it) => {
        const on = it.key === active;
        const pulse = hint === it.key && !on;
        return (
          <button
            key={it.key}
            onClick={() => pick(it.key)}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              color: on ? "var(--text)" : "var(--hint)",
              position: "relative",
              padding: 6,
              fontFamily: "var(--font-text)",
              fontWeight: on ? 700 : 500,
            }}
          >
            <div style={{ position: "relative", display: "flex" }}>
              <NavIcon name={it.key} active={on} />
              {pulse && (
                <span
                  className="dot-pulse"
                  aria-label="Tap to get started"
                  style={{
                    position: "absolute",
                    top: -2,
                    right: -4,
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "var(--accent, #F0B400)",
                  }}
                />
              )}
            </div>
            <span style={{ fontSize: 11, letterSpacing: "-0.005em" }}>
              {it.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
